import { useState } from 'react';
import { ChevronDown, Palette } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { EDITOR_COLOR_SCHEMES, type EditorColorSelection } from '../../../../core/theme/monacoColorSchemes';
import { getSwatchesForSelection } from '../../../../core/theme/customEditorColors';
import { EditorColorSchemeDialog } from './EditorColorSchemeDialog';

function labelFor(id: EditorColorSelection): string {
  if (id === 'custom') return 'Custom';
  return EDITOR_COLOR_SCHEMES.find((scheme) => scheme.id === id)?.label ?? id;
}

/** 4 small round swatches (keyword, string, number, comment) for the current selection, in the current app mode. */
function SchemeSwatches({ colors }: { colors: string[] }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5">
      {colors.map((color, i) => (
        <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

/**
 * Appearance row: opens the Editor colors dialog (Slice 3 dialog iteration
 * — presets + Custom used to live in an inline Select with the Custom
 * picker panel directly below the row; both now live in
 * `EditorColorSchemeDialog`).
 */
export function EditorColorSchemeSection() {
  const editorColorScheme = useAppStore((state) => state.editorColorScheme);
  const customEditorColors = useAppStore((state) => state.customEditorColors);
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  const { track } = useAnalytics();
  const [dialogOpen, setDialogOpen] = useState(false);

  const swatches = getSwatchesForSelection(editorColorScheme, isDarkMode, customEditorColors);

  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
      <div className="flex items-center gap-3">
        <Palette className="h-5 w-5 text-muted-foreground" />
        <div className="grid gap-0.5">
          <Label className="cursor-default">Editor colors</Label>
          <div className="text-xs text-muted-foreground">
            Syntax highlighting colors for SQL editors.
          </div>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-[200px] justify-between font-normal"
        data-testid="editor-color-scheme-open"
        onClick={() => {
          track('feature:editor_colors_dialog_opened');
          setDialogOpen(true);
        }}
      >
        <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
          <span className="truncate">{labelFor(editorColorScheme)}</span>
          <SchemeSwatches colors={swatches} />
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </Button>
      <EditorColorSchemeDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
